import '../sass/main.scss';
import './global-utils.js';
import { scheduleConsentPopup } from './consent-popup.js';

document.addEventListener('DOMContentLoaded', () => {
   const siteHeader = document.querySelector('.site-header');
   const mainGrid = document.querySelector('.main-grid');
   const scrolledElements = Array.from(document.querySelectorAll(
       '.site-header, .site-branding a svg, .menu-item a, .live-search-results'
   ));
   let scrollThreshold = 0;
   let isTicking = false;
   let lastScrolledState = null;

   if (!siteHeader || !scrolledElements.length) {
      return;
   }

   const updateScrollThreshold = () => {
      scrollThreshold = siteHeader.offsetHeight / 2;
   };

   const handleMainGridMargin = () => {
      if (!mainGrid) return;

      mainGrid.classList.toggle(
          'main-grid--header-scrolled',
          siteHeader.classList.contains('scrolled')
      );
   };

   const handleScroll = () => {
      const scrollHeight = window.scrollY || document.documentElement.scrollTop || 0;
      const isScrolled = scrollHeight > scrollThreshold;

      if (isScrolled === lastScrolledState) {
         return;
      }

      lastScrolledState = isScrolled;

      scrolledElements.forEach((element) => {
         element.classList.toggle('scrolled', isScrolled);
      });

      handleMainGridMargin();
   };

   const requestScrollUpdate = () => {
      if (isTicking) {
         return;
      }

      isTicking = true;

      window.requestAnimationFrame(() => {
         handleScroll();
         isTicking = false;
      });
   };

   updateScrollThreshold();
   window.addEventListener('resize', updateScrollThreshold, { passive: true });
   window.addEventListener('scroll', requestScrollUpdate, { passive: true });
   handleScroll();
});

// Lazy decorative videos
document.addEventListener('DOMContentLoaded', () => {
   const lazyVideos = Array.from(document.querySelectorAll('video[data-lazy-video]'));

   if (!lazyVideos.length) {
      return;
   }

   const scheduleAfterLoad = (callback) => {
      const runWhenIdle = () => {
         if ('requestIdleCallback' in window) {
            window.requestIdleCallback(callback, { timeout: 3000 });
            return;
         }

         window.setTimeout(callback, 1200);
      };

      if (document.readyState === 'complete') {
         runWhenIdle();
         return;
      }

      window.addEventListener('load', runWhenIdle, { once: true });
   };

   const loadVideo = (video) => {
      if (!video || video.dataset.lazyVideoLoaded) {
         return;
      }

      video.querySelectorAll('source[data-src]').forEach((source) => {
         source.src = source.dataset.src;
         source.removeAttribute('data-src');
      });

      video.dataset.lazyVideoLoaded = 'true';
      video.load();

      if (video.autoplay) {
         video.play().catch(() => {});
      }
   };

   const regularLazyVideos = lazyVideos.filter((video) => !video.hasAttribute('data-lazy-video-after-load'));
   const afterLoadVideos = lazyVideos.filter((video) => video.hasAttribute('data-lazy-video-after-load'));

   afterLoadVideos.forEach((video) => {
      scheduleAfterLoad(() => loadVideo(video));
   });

   if (!('IntersectionObserver' in window)) {
      regularLazyVideos.forEach(loadVideo);
      return;
   }

   const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
         if (!entry.isIntersecting) {
            return;
         }

         loadVideo(entry.target);
         observer.unobserve(entry.target);
      });
   }, {
      rootMargin: '300px 0px',
   });

   regularLazyVideos.forEach((video) => observer.observe(video));
});

// Auto load popup
document.addEventListener('DOMContentLoaded', () => {
   const popupOverlays = Array.from(document.querySelectorAll('.nav-popup-overlay'));
   const popupCloseButtons = Array.from(document.querySelectorAll('.nav-close-popup'));
   const fancyboxTriggers = document.querySelector('[data-fancybox]');
   const hasAutoloadPopup = document.body.classList.contains('page-template-front-page')
       && document.querySelector('.nav-popup-overlay--autoload');

   if (!popupOverlays.length && !popupCloseButtons.length && !fancyboxTriggers && !hasAutoloadPopup) {
      return;
   }

   const closePopup = (popup) => {
      if (!popup) return;

      popup.classList.remove('is-visible');
      popup.setAttribute('aria-hidden', 'true');
      window.setTimeout(() => {
         popup.style.display = 'none';
      }, 250);
   };

   const openPopup = (popup) => {
      if (!popup) return;

      popup.style.display = 'block';
      popup.setAttribute('aria-hidden', 'false');
      window.requestAnimationFrame(() => {
         popup.classList.add('is-visible');
      });
   };

   const loadFancybox = () => {
      const config = window.michallukasFancybox || {};

      if (window.Fancybox) {
         return Promise.resolve(window.Fancybox);
      }

      if (!config.cssUrl || !config.jsUrl) {
         return Promise.resolve(null);
      }

      if (!document.querySelector('link[data-fancybox-css]')) {
         const link = document.createElement('link');
         link.rel = 'stylesheet';
         link.href = config.cssUrl;
         link.dataset.fancyboxCss = '';
         document.head.appendChild(link);
      }

      if (window.michallukasFancyboxPromise) {
         return window.michallukasFancyboxPromise;
      }

      window.michallukasFancyboxPromise = new Promise((resolve, reject) => {
         const script = document.createElement('script');
         script.src = config.jsUrl;
         script.async = true;
         script.dataset.fancyboxJs = '';
         script.onload = () => resolve(window.Fancybox || null);
         script.onerror = reject;
         document.body.appendChild(script);
      });

      return window.michallukasFancyboxPromise;
   };

   const openFancyboxGallery = (trigger) => {
      loadFancybox().then((Fancybox) => {
         if (!Fancybox) {
            window.location.href = trigger.href;
            return;
         }

         const group = trigger.getAttribute('data-fancybox');
         const escapedGroup = window.CSS && typeof window.CSS.escape === 'function'
             ? window.CSS.escape(group || '')
             : (group || '').replace(/"/g, '\\"');
         const selector = group
             ? `[data-fancybox="${escapedGroup}"]`
             : '[data-fancybox]';
         const links = Array.from(document.querySelectorAll(selector)).filter((link) => link.href);
         const items = links.map((link) => ({
            src: link.href,
            type: 'image',
         }));
         const startIndex = Math.max(links.indexOf(trigger), 0);

         Fancybox.show(items, {
            startIndex,
         });
      }).catch(() => {
         window.location.href = trigger.href;
      });
   };

   if (fancyboxTriggers) {
      document.addEventListener('click', (event) => {
         const trigger = event.target.closest('[data-fancybox]');

         if (!trigger) {
            return;
         }

         event.preventDefault();
         openFancyboxGallery(trigger);
      });
   }

   popupCloseButtons.forEach((button) => {
      button.addEventListener('click', () => {
         closePopup(button.closest('.nav-popup-overlay'));
      });
   });

   popupOverlays.forEach((popup) => {
      popup.addEventListener('click', (event) => {
         if (event.target === popup) {
            closePopup(popup);
         }
      });
   });

   document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      document.querySelectorAll('.nav-popup-overlay.is-visible').forEach(closePopup);
   });

   window.addEventListener('load', () => {
      if (!hasAutoloadPopup) {
         return;
      }

      const popup = document.querySelector('.nav-popup-overlay--autoload');

      const delay = parseInt(popup.dataset.popupDelay, 10) || 8000;

      scheduleConsentPopup({
         open: () => openPopup(popup),
         close: () => closePopup(popup),
         delay,
      });
   });
});
